import * as THREE from "three";
import type { UnitLook } from "../unitFx";

/**
 * Boss hero moments (art review units F7 and "Hero moments" 1–2; ART_WOW_PASS Phase 2): the pure timing curves and
 * the listener lists the rest of the client subscribes to. `EnemyViews` runs the per-frame work with these.
 *
 * Arrival: the boss rises out of the ground with an overshoot (1.2 s, ease-out-back: it heaves a little above the
 * road and settles), scaling 0.6 → 1.1 → 1.0, with a short slow-motion dip and a stomp of trauma and dust as it
 * clears the surface. Its rune ring fades in under it.
 * Presence: every footfall of the walk throws dust and a small shake (the stone and iron bosses shake more); before a
 * blocked boss strikes the troop holding it, a red telegraph disc fills on the ground over the last 0.5 s.
 * Phase change at 50 % hp: the rim flares white-hot, a 1.0 → 1.08 → 1.0 scale pop, a flash, a short hit-stop and a
 * hotter rim from then on.
 * Fall: 250 ms hit-stop, the rim white-hot, glowing cracks spread from the chest over 0.6 s, then the body bursts —
 * screen flash — and dissolves along a wide ember band; the reward coins fly from the body into the HUD points pill.
 */

/** A boss view, as the listeners see it. `root` stands at the boss's feet. */
export interface BossHandle { readonly id: number; readonly archId: string; readonly root: THREE.Object3D; readonly body: THREE.Object3D; readonly look: UnitLook | null }

/**
 * The boss event lists (PHASE2_BRIEF "Boss events"): look (dusk), vfx (arrival/fall) and ui (DOM boss bar) push a
 * listener; units emits. `onBossHp` fires only when the fraction moves (not every tick).
 */
export class BossEvents {
  readonly onBossSpawn: ((boss: BossHandle) => void)[] = [];
  readonly onBossHp: ((frac: number, boss: BossHandle) => void)[] = [];
  readonly onBossPhase: ((phase: number, boss: BossHandle) => void)[] = [];
  readonly onBossDeath: ((boss: BossHandle, worldPos: THREE.Vector3) => void)[] = [];
  /**
   * The boss left without a fall: it walked into the fortress (EnemyLeaked) and its view went straight back to the
   * pool. Without this the DOM boss bar, the dusk and the boss bed waited for a death that never came and stayed up
   * for the rest of the level (M30 #3).
   */
  readonly onBossGone: ((boss: BossHandle) => void)[] = [];

  spawn(b: BossHandle): void { for (const f of this.onBossSpawn) f(b); }
  hp(frac: number, b: BossHandle): void { for (const f of this.onBossHp) f(frac, b); }
  phase(n: number, b: BossHandle): void { for (const f of this.onBossPhase) f(n, b); }
  death(b: BossHandle, at: THREE.Vector3): void { for (const f of this.onBossDeath) f(b, at); }
  gone(b: BossHandle): void { for (const f of this.onBossGone) f(b); }
}

export const BOSS = {
  /** Arrival: rise time, how deep it starts (× its height), the moment it clears the ground (stomp). */
  emergeSeconds: 1.2, emergeDepth: 0.9, stompAt: 0.42,
  /** Arrival slow-mo dip: a short stop then HitStop's ramp (0.4 → 1× over 0.4 s). */
  dipMs: 40,
  stompTrauma: 0.25,
  /** Footfall shake: heavy bosses (stone, iron) and the rest. */
  footTrauma: { heavy: 0.2, other: 0.1 },
  heavy: new Set(["boss_colossus", "boss_ironclad"]),
  /** Attack telegraph: shown over the last N ticks before a blocked boss strikes; disc width in cells. */
  telegraphTicks: 10, telegraphWidth: 0.8,
  /** Phase change at this hp fraction: rim flare, scale pop, hit-stop, trauma; the rim stays this much hotter. */
  phaseAt: 0.5, phasePop: 0.08, phasePopSeconds: 0.35, phaseHitStopMs: 50, phaseTrauma: 0.3, phaseRim: 0.35,
  /** Fall: cracks, then the burst (screen flash) and the dissolve; coins leave for the HUD at `coinsAt`. */
  crackSeconds: 0.6, dissolveSeconds: 0.9, coinsAt: 0.7, coins: 10,
  flash: { color: "#fff1cc", peak: 0.35, ms: 220 },
} as const;

/** ease-out-back (c1 = 1.70158): overshoots to ≈ 1.10 at u ≈ 0.58, lands on 1 at u = 1. */
export function easeOutBack(u: number): number {
  const c1 = 1.70158, c3 = c1 + 1, x = Math.max(0, Math.min(1, u)) - 1;
  return 1 + c3 * x * x * x + c1 * x * x;
}

/**
 * The arrival at `t` seconds: `rise` is the vertical offset in units of the boss's height (−depth → a little above 0
 * → 0), `scale` its size multiplier (0.6 → 1.1 → 1.0).
 */
export function emerge(t: number): { rise: number; scale: number } {
  const e = easeOutBack(t / BOSS.emergeSeconds);
  return { rise: -BOSS.emergeDepth * (1 - e), scale: 0.6 + 0.4 * e + 0.6 * Math.max(0, e - 1) };
}

/** Boss phase for a health fraction: 1 above half, 2 at or below. */
export function bossPhase(frac: number): number { return frac <= BOSS.phaseAt ? 2 : 1; }

/**
 * Telegraph fill for a blocked boss: `cooldown` ticks until its next strike (0 = not blocked). −1 when nothing should
 * show; otherwise 0 → 1 over the last `window` ticks.
 */
export function telegraphProgress(cooldown: number, window: number = BOSS.telegraphTicks): number {
  if (cooldown <= 0 || cooldown > window) return -1;
  return 1 - (cooldown - 1) / window;
}

/** True when the walk loop's phase passed a footfall (0 or ½) between two frames. */
export function footfallCrossed(prev: number, cur: number): boolean {
  if (cur < prev) return true;                          // wrapped past 0
  return prev < 0.5 && cur >= 0.5;
}

/** Phase-change scale pop: 0 → +pop → 0 over `phasePopSeconds` (a sine hump). */
export function phasePop(t: number): number {
  if (t <= 0 || t >= BOSS.phasePopSeconds) return 0;
  return BOSS.phasePop * Math.sin((t / BOSS.phasePopSeconds) * Math.PI);
}
